import React, { Component } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { InputSwitch } from 'primereact/inputswitch';
import { Calendar } from 'primereact/calendar';
import Service from '../../services';
import LoadingComponent from '../../loadingComponent';
import { baseUrlAdmin } from '../../../store/apiConstants';
import ClassetInputText from '../../../classetComponents/classetInputText';
import ClassetDropdown from '../../../classetComponents/classetDropDown';
import ClassetCalendar from '../../../classetComponents/classetCalender';
import ClassetTextArea from '../../../classetComponents/classetTextArea';
import BranchToSectionMultiDDTillSec from '../../BaseDropdownComponents/BranchToSectionMultiDDTillSec';
import { AddIconInButtonFull, CalenderIcon, ClockIcon, CodeLinkIcon, MailIcon } from '../../svgIcons';
import './styles.scss';

// Event type options
const EVENT_TYPES = [
    { value: 'AE', label: "Academic Event" },
    { value: 'SE', label: "Sports Event" },
    { value: 'CE', label: "Cultural Event" },
    { value: 'ME', label: "Miscellaneous Event" },
    { value: 'EE', label: "Examination" },
    { value: 'HE', label: 'Holiday' }
];

// Repeat options for recurring events
const REPEAT_OPTIONS = [
    { value: 'DAILY', label: "Daily" },
    { value: 'WEEKLY', label: "Weekly" },
    { value: 'MONTHLY', label: "Monthly" },
    { value: 'YEARLY', label: "Yearly" },
];

// Event colors based on event type
const EVENT_COLORS = {
    'AE': '#0077B6', // Academic Event
    'SE': '#FAB710', // Sports Event
    'CE': '#A020F0', // Cultural Event
    'ME': '#E6E8EA', // Miscellaneous Event
    'EE': '#BF0031', // Examination
    'HE': '#268900', // Holiday
    'default': '#076EFF'
};

class AcademicCalendar extends Component {
    constructor(props) {
        super(props);

        this.state = {
            events: [],
            loading: false,
            showEventDialog: false,
            showActionDialog: false,
            showDeleteDialog: false,
            isEditMode: false,
            selectedEvent: null,
            eventForm: {
                title: '',
                eventType: null,
                eventDate: null,
                startTime: null,
                endTime: null,
                fdEvent: false,
                recurring: false,
                eventDescription: '',
                venue: '',
                repeat: null,
                recStartDate: null,
                recEndDate: null,
            },
            dropdownsData: {
                branchIds: [],
                boardIds: [],
                classIds: [],
                sectionIds: [],
                branchNames: [],
                boardNames: [],
                gradeNames: [],
                sectionNames: []
            },
            validationErrors: {}
        };

        this.toast = null;
        this.service = new Service();
    }

    componentDidMount() {
        console.log('AcademicCalendar mounted - NEW VERSION with callback refs');
        this.fetchEvents();
    }

    // Fetch events from API
    fetchEvents = async () => {
        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const url = `${baseUrlAdmin}/event-calender/calenders?academicYear=${academicYear}`;
            const response = await this.service.get(url, true);

            if (response && response.res && response.res.data) {
                // Process events to handle recurring events with RRule format
                const processedEvents = this.processEventsForCalendar(response.res.data);
                this.setState({ events: processedEvents });
            } else {
                this.setState({ events: [] });
                this.showToast('error', 'Error', response?.res?.message || 'Failed to fetch events');
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            this.showToast('error', 'Error', 'Failed to fetch events');
            this.setState({ events: [] });
        } finally {
            this.setState({ loading: false });
        }
    };

    // Process events for the calendar, converting recurring events to RRule format
    processEventsForCalendar = (apiEvents) => {
        return apiEvents.map(event => {
            // Make a copy of the event to avoid modifying the original
            const processedEvent = { ...event };

            // Always set color properties regardless of event type
            // Use eventType color first, fall back to color property, then default
            const eventColor = event.eventType && EVENT_COLORS[event.eventType]
                ? EVENT_COLORS[event.eventType]
                : (event.color || EVENT_COLORS.default);

            processedEvent.backgroundColor = eventColor;
            processedEvent.borderColor = eventColor;

            // Add key event details as class names for CSS targeting
            const classNames = [];
            if (event.eventType) classNames.push(`event-type-${event.eventType.toLowerCase()}`);
            if (event.recurring) classNames.push('recurring-event');
            if (!event.allDay && !event.fdEvent) classNames.push('timed-event');

            processedEvent.classNames = classNames;

            // Store ALL event properties in extendedProps for ALL events (not just recurring)
            // This ensures time values are always available
            processedEvent.extendedProps = {
                ...event,
                backgroundColor: eventColor,
                borderColor: eventColor,
                // Make sure startTime and endTime are always included
                startTime: event.startTime || null,
                endTime: event.endTime || null
            };

            // Handle recurring events
            if (event.recurring && event.repeat) {
                processedEvent.title = event.title;
                processedEvent.allDay = event.allDay || event.fdEvent;

                // Create an RRule definition for this event
                const rruleOptions = this.createRRuleOptions(event);
                if (rruleOptions) {
                    processedEvent.rrule = rruleOptions;

                    // For recurring events with times, set duration on the event (not in rrule)
                    if (!event.allDay && !event.fdEvent && event.endTime && event.startTime) {
                        const startTimeMoment = moment(event.startTime);
                        const endTimeMoment = moment(event.endTime);
                        const durationMs = endTimeMoment.diff(startTimeMoment);
                        processedEvent.duration = { milliseconds: durationMs };
                    }

                    // For recurring events, we use the rrule instead of start/end
                    delete processedEvent.start;
                    delete processedEvent.end;
                }
            }

            return processedEvent;
        });
    };

    // Create RRule options based on the event recurrence pattern
    createRRuleOptions = (event) => {
        if (!event.recurring || !event.repeat || !event.recStartDate || !event.recEndDate) {
            return null;
        }

        // Parse start and end dates
        const startDate = event.start || event.recStartDate;

        // DO NOT add an extra day to the end date to make it inclusive
        // This was causing the extra day to appear
        let endDate = event.end || event.recEndDate;

        // Base rrule options for all recurrence types
        const rruleOptions = {
            dtstart: startDate,
            until: endDate
        };

        // Rest of the method remains the same...

        // Set frequency based on repeat value
        switch (event.repeat) {
            case 'DAILY':
                rruleOptions.freq = 'daily';
                break;
            case 'WEEKLY':
                rruleOptions.freq = 'weekly';
                // Get the day of week from the start date (0=SU, 1=MO, etc.)
                const startMoment = moment(startDate);
                const dayOfWeek = startMoment.day();
                // Get the day name for RRule (SU, MO, TU, etc.)
                const daysOfWeek = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];
                rruleOptions.byweekday = [daysOfWeek[dayOfWeek]];
                break;
            case 'MONTHLY':
                rruleOptions.freq = 'monthly';
                // Use the same day of month
                rruleOptions.bymonthday = [moment(startDate).date()];
                break;
            case 'YEARLY':
                rruleOptions.freq = 'yearly';
                break;
            default:
                return null;
        }

        // For events with times, include the time in the dtstart
        if (!event.allDay && !event.fdEvent) {
            // If there are specific start and end times, include them
            if (event.startTime) {
                const startMoment = moment(startDate);
                const startTimeMoment = moment(event.startTime);
                rruleOptions.dtstart = moment({
                    year: startMoment.year(),
                    month: startMoment.month(),
                    date: startMoment.date(),
                    hour: startTimeMoment.hour(),
                    minute: startTimeMoment.minute(),
                    second: 0
                }).toDate();
            }

            // NOTE: Duration should NOT be in rrule options
            // It should be set on the event object itself (handled in processEventsForCalendar)
        }

        return rruleOptions;
    };

    // Handle event click on calendar - directly open edit dialog
    handleEventClick = (info) => {
        const eventData = info.event;
        this.setState(
            { selectedEvent: eventData },
            () => this.openEditEventDialog()
        );
    };

    // Open event dialog for adding a new event
    openAddEventDialog = () => {
        this.setState({
            eventForm: {
                title: '',
                eventType: null,
                eventDate: null,
                startTime: null,
                endTime: null,
                fdEvent: false,
                recurring: false,
                eventDescription: '',
                venue: '',
                repeat: null,
                recStartDate: null,
                recEndDate: null
            },
            dropdownsData: {
                branchIds: [],
                boardIds: [],
                classIds: [],
                sectionIds: [],
                branchNames: [],
                boardNames: [],
                gradeNames: [],
                sectionNames: []
            },
            validationErrors: {},
            isEditMode: false,
            showEventDialog: true
        });
    };

    openEditEventDialog = () => {
        const { selectedEvent } = this.state;
        if (!selectedEvent) return;

        const extProps = selectedEvent.extendedProps || {};

        const extractTime = (timeString, fallbackDate) => {
            if (!timeString) {
                if (fallbackDate) {
                    const parsedDate = new Date(fallbackDate);
                    if (!isNaN(parsedDate.getTime())) {
                        return new Date(0, 0, 0, parsedDate.getHours(), parsedDate.getMinutes(), 0);
                    }
                }
                return null;
            }

            try {
                const parsedTime = new Date(timeString);
                if (!isNaN(parsedTime.getTime())) {
                    return parsedTime;
                }
                return null;
            } catch (e) {
                return null;
            }
        };

        let startTime = null;
        let endTime = null;

        if (extProps.startTime) {
            startTime = extractTime(extProps.startTime);
        }

        if (extProps.endTime) {
            endTime = extractTime(extProps.endTime);
        }

        if (!startTime && !selectedEvent.allDay && !extProps.fdEvent) {
            const eventStart = selectedEvent.start || extProps.start;
            if (eventStart) {
                startTime = extractTime(null, eventStart);
            }
        }

        if (!endTime && !selectedEvent.allDay && !extProps.fdEvent) {
            const eventEnd = selectedEvent.end || extProps.end;
            if (eventEnd) {
                endTime = extractTime(null, eventEnd);
            }
        }

        // Handle both old and new event formats
        // Old format: branchId, boardId, classId, sectionId (singular fields)
        // New format: branches, boards, grades, sections (array fields)
        let dropdownsData = {
            branchIds: [],
            boardIds: [],
            classIds: [],
            sectionIds: [],
            branchNames: [],
            boardNames: [],
            gradeNames: [],
            sectionNames: []
        };

        // Check if we have new format (arrays)
        if (extProps.branches && extProps.branches.length > 0) {
            dropdownsData = {
                branchIds: extProps.branches.map(b => b.id),
                boardIds: extProps.boards?.map(b => b.id) || [],
                classIds: extProps.grades?.map(g => g.id) || [],
                sectionIds: extProps.sections?.map(s => s.id) || [],
                branchNames: extProps.branches.map(b => b.name),
                boardNames: extProps.boards?.map(b => b.name) || [],
                gradeNames: extProps.grades?.map(g => g.name) || [],
                sectionNames: extProps.sections?.map(s => s.name) || []
            };
        }
        // Check if we have old format (singular fields)
        else if (extProps.branchId || extProps.boardId || extProps.classId || extProps.sectionId) {
            dropdownsData = {
                branchIds: extProps.branchId ? [extProps.branchId] : [],
                boardIds: extProps.boardId ? [extProps.boardId] : [],
                classIds: extProps.classId ? [extProps.classId] : [],
                sectionIds: extProps.sectionId ? [extProps.sectionId] : [],
                branchNames: extProps.selectedbranchName ? [extProps.selectedbranchName] : [],
                boardNames: extProps.selectedBoardName ? [extProps.selectedBoardName] : [],
                gradeNames: extProps.selectedClassName ? [extProps.selectedClassName] : [],
                sectionNames: extProps.selectedsectionName ? [extProps.selectedsectionName] : []
            };
        }

        console.log('[openEditEventDialog] Dropdown data:', {
            branches: extProps.branches,
            boards: extProps.boards,
            grades: extProps.grades,
            sections: extProps.sections,
            oldFormat: {
                branchId: extProps.branchId,
                boardId: extProps.boardId,
                classId: extProps.classId,
                sectionId: extProps.sectionId,
                selectedbranchName: extProps.selectedbranchName,
                selectedBoardName: extProps.selectedBoardName,
                selectedClassName: extProps.selectedClassName,
                selectedsectionName: extProps.selectedsectionName
            },
            dropdownsData
        });

        let eventDate = null;
        if (selectedEvent.start) {
            eventDate = new Date(selectedEvent.start);
        } else if (extProps.start) {
            eventDate = new Date(extProps.start);
        } else if (extProps.recStartDate && !extProps.recurring) {
            eventDate = new Date(extProps.recStartDate);
        }

        const eventForm = {
            title: selectedEvent.title || '',
            eventType: extProps.eventType || null,
            eventDate: eventDate,
            startTime: startTime,
            endTime: endTime,
            fdEvent: Boolean(selectedEvent.allDay || extProps.fdEvent),
            recurring: Boolean(extProps.recurring),
            eventDescription: extProps.eventDescription || '',
            venue: extProps.venue || '',
            repeat: extProps.repeat || null,
            recStartDate: extProps.recStartDate ? new Date(extProps.recStartDate) : null,
            recEndDate: extProps.recEndDate ? new Date(extProps.recEndDate) : null
        };

        this.setState({
            dropdownsData: {
                branchIds: [],
                boardIds: [],
                classIds: [],
                sectionIds: [],
                branchNames: [],
                boardNames: [],
                gradeNames: [],
                sectionNames: []
            },
            eventForm,
            isEditMode: true,
            showActionDialog: false,
            showEventDialog: true
        }, () => {
            // Set dropdown data in next tick to ensure component detects the change
            setTimeout(() => {
                console.log('[openEditEventDialog] Setting dropdownsData:', dropdownsData);
                this.setState({ dropdownsData });
            }, 0);
        });
    };

    deleteEvent = async () => {
        const { selectedEvent } = this.state;
        if (!selectedEvent || !selectedEvent.extendedProps._id) return;

        this.setState({ loading: true });
        try {
            const url = `${baseUrlAdmin}/event-calender/${selectedEvent.extendedProps._id}`;
            const response = await this.service.delete(url, true);

            if (response && response.status && response.res.status) {
                this.showToast('success', 'Success', 'Event deleted successfully');
                this.setState({
                    showDeleteDialog: false,
                    showActionDialog: false
                });
                await this.fetchEvents(); // Refresh events
            } else {
                this.showToast('error', 'Error', response?.res?.message || 'Failed to delete event');
            }
        } catch (error) {
            console.error('Error deleting event:', error);
            this.showToast('error', 'Error', 'Failed to delete event');
        } finally {
            this.setState({ loading: false });
        }
    };

    // Handle form input changes
    handleInputChange = (value, field) => {
        this.setState(prevState => {
            const updatedForm = {
                ...prevState.eventForm,
                [field]: value
            };

            // Handle event type changes
            if (field === 'eventType') {
                // If event type is changed to HE (Holiday) or EE (Examination), automatically set fdEvent to true
                if (value === 'HE' || value === 'EE') {
                    updatedForm.fdEvent = true;
                }
                // If event type is changed from HE or EE to another type, clear fdEvent
                else if (prevState.eventForm.eventType === 'HE' || prevState.eventForm.eventType === 'EE') {
                    updatedForm.fdEvent = false;
                }
            }

            return {
                eventForm: updatedForm,
                validationErrors: {
                    ...prevState.validationErrors,
                    [field]: undefined
                }
            };
        });
    };

    setDropdownValues = (dropdownsData) => {
        const safeDropdownsData = dropdownsData || {};

        this.setState({
            dropdownsData: {
                branchIds: safeDropdownsData.branchIds || [],
                boardIds: safeDropdownsData.boardIds || [],
                classIds: safeDropdownsData.classIds || [],
                sectionIds: safeDropdownsData.sectionIds || [],
                branchNames: safeDropdownsData.branchNames || [],
                boardNames: safeDropdownsData.boardNames || [],
                gradeNames: safeDropdownsData.gradeNames || [],
                sectionNames: safeDropdownsData.sectionNames || []
            }
        });
    }

    // Format date and time for API
    formatDateTime = (date, time) => {
        if (!date) return null;

        if (time) {
            const dateMoment = moment(date);
            const timeMoment = moment(time);

            return moment({
                year: dateMoment.year(),
                month: dateMoment.month(),
                date: dateMoment.date(),
                hour: timeMoment.hour(),
                minute: timeMoment.minute(),
                second: 0
            }).toISOString();
        }

        return moment(date).format('YYYY-MM-DD');
    };

    validateForm = () => {
        const { eventForm, dropdownsData } = this.state;
        const errors = {};

        if (!eventForm.title) errors.title = "Event name is required";
        if (!eventForm.eventType) errors.eventType = "Event type is required";

        if (!eventForm.recurring && !eventForm.eventDate) {
            errors.eventDate = "Date is required";
        }

        if (eventForm.recurring) {
            if (!eventForm.recStartDate) errors.recStartDate = "Start date is required";
            if (!eventForm.recEndDate) errors.recEndDate = "End date is required";
            if (!eventForm.repeat) errors.repeat = "Repeat option is required";

            // Validate that end date is at least one day after start date
            if (eventForm.recStartDate && eventForm.recEndDate) {
                const startDate = moment(eventForm.recStartDate).startOf('day');
                const endDate = moment(eventForm.recEndDate).startOf('day');
                const daysDifference = endDate.diff(startDate, 'days');

                if (daysDifference < 1) {
                    errors.recEndDate = "End date must be at least one day after start date";
                }
            }

            // Skip time validation for HE (Holiday) and EE (Examination) event types
            if (!eventForm.fdEvent && eventForm.eventType !== 'HE' && eventForm.eventType !== 'EE') {
                if (!eventForm.startTime) errors.startTime = "Start time is required";
                if (!eventForm.endTime) errors.endTime = "End time is required";
            }
        }

        // Skip time validation for HE (Holiday) and EE (Examination) event types
        if (!eventForm.recurring && !eventForm.fdEvent && eventForm.eventType !== 'HE' && eventForm.eventType !== 'EE') {
            if (!eventForm.startTime) errors.startTime = "Start time is required";
            if (!eventForm.endTime) errors.endTime = "End time is required";
        }

        if (!eventForm.eventDescription || eventForm.eventDescription.trim() === '') {
            errors.eventDescription = "Event description is required";
        }

        if (!eventForm.venue || eventForm.venue.trim() === '') {
            errors.venue = "Venue is required";
        }

        if (!dropdownsData.boardIds || dropdownsData.boardIds.length === 0) {
            errors.boardIds = "At least one board must be selected";
        }

        if (!dropdownsData.branchIds || dropdownsData.branchIds.length === 0) {
            errors.branchIds = "At least one branch must be selected";
        }

        if (!dropdownsData.classIds || dropdownsData.classIds.length === 0) {
            errors.classIds = "At least one grade must be selected";
        }

        if (!dropdownsData.sectionIds || dropdownsData.sectionIds.length === 0) {
            errors.sectionIds = "At least one section must be selected";
        }

        this.setState({ validationErrors: errors });
        return Object.keys(errors).length === 0;
    };

    saveEvent = async () => {
        if (!this.validateForm()) {
            this.showToast('error', 'Validation Error', 'Please fill all required fields');
            return;
        }

        this.setState({ loading: true });

        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const { isEditMode, selectedEvent, eventForm, dropdownsData = {} } = this.state;
            const eventId = isEditMode && selectedEvent?.extendedProps?._id;

            let payload = {
                title: eventForm.title || '',
                eventType: eventForm.eventType || '',
                eventDescription: eventForm.eventDescription || '',
                venue: eventForm.venue || '',
                fdEvent: Boolean(eventForm.fdEvent),
                recurring: Boolean(eventForm.recurring),
                academicYear,
            };

            payload.color = EVENT_COLORS[eventForm.eventType] || EVENT_COLORS.default;

            const branches = [];
            const boards = [];
            const grades = [];
            const sections = [];

            if (dropdownsData.branchIds && dropdownsData.branchIds.length > 0) {
                dropdownsData.branchIds.forEach((id, index) => {
                    branches.push({
                        id: id,
                        name: dropdownsData.branchNames?.[index] || ''
                    });
                });
            }

            if (dropdownsData.boardIds && dropdownsData.boardIds.length > 0) {
                dropdownsData.boardIds.forEach((id, index) => {
                    boards.push({
                        id: id,
                        name: dropdownsData.boardNames?.[index] || ''
                    });
                });
            }

            if (dropdownsData.classIds && dropdownsData.classIds.length > 0) {
                dropdownsData.classIds.forEach((id, index) => {
                    grades.push({
                        id: id,
                        name: dropdownsData.gradeNames?.[index] || ''
                    });
                });
            }

            if (dropdownsData.sectionIds && dropdownsData.sectionIds.length > 0) {
                dropdownsData.sectionIds.forEach((id, index) => {
                    sections.push({
                        id: id,
                        name: dropdownsData.sectionNames?.[index] || ''
                    });
                });
            }

            payload.branches = branches;
            payload.boards = boards;
            payload.grades = grades;
            payload.sections = sections;

            if (eventForm.recurring) {
                payload.repeat = eventForm.repeat;
                payload.recStartDate = moment(eventForm.recStartDate).toISOString();
                payload.recEndDate = moment(eventForm.recEndDate).toISOString();

                const startMoment = moment(eventForm.recStartDate);
                const endMoment = moment(eventForm.recEndDate);

                if (eventForm.fdEvent || ['HE', 'EE'].includes(eventForm.eventType)) {
                    payload.start = startMoment.startOf('day').toISOString();
                    payload.end = endMoment.endOf('day').toISOString();
                    payload.allDay = true;
                } else {
                    const startTime = moment(eventForm.startTime);
                    const endTime = moment(eventForm.endTime);

                    payload.start = startMoment.hour(startTime.hour()).minute(startTime.minute()).second(0).toISOString();
                    payload.end = endMoment.hour(endTime.hour()).minute(endTime.minute()).second(0).toISOString();
                    payload.startTime = startTime.toISOString();
                    payload.endTime = endTime.toISOString();
                    payload.allDay = false;
                }

                switch (eventForm.repeat) {
                    case 'WEEKLY':
                        payload.weeklyDayOfWeek = startMoment.day();
                        break;
                    case 'MONTHLY':
                        payload.monthlyDayOfMonth = startMoment.date();
                        break;
                    case 'YEARLY':
                        payload.yearlyMonth = startMoment.month() + 1;
                        payload.yearlyDay = startMoment.date();
                        break;
                }
            } else {
                const eventMoment = moment(eventForm.eventDate);

                if (eventForm.fdEvent || ['HE', 'EE'].includes(eventForm.eventType)) {
                    payload.start = eventMoment.startOf('day').toISOString();
                    payload.end = eventMoment.clone().add(1, 'day').startOf('day').toISOString();
                    payload.allDay = true;
                } else {
                    const startTime = moment(eventForm.startTime);
                    const endTime = moment(eventForm.endTime);

                    payload.start = eventMoment.hour(startTime.hour()).minute(startTime.minute()).second(0).toISOString();
                    payload.end = eventMoment.hour(endTime.hour()).minute(endTime.minute()).second(0).toISOString();
                    payload.startTime = startTime.toISOString();
                    payload.endTime = endTime.toISOString();
                    payload.allDay = false;
                }
            }

            const url = eventId
                ? `${baseUrlAdmin}/event-calender/${eventId}`
                : `${baseUrlAdmin}/event-calender/create`;

            const method = eventId ? 'put' : 'post';
            const response = await this.service[method](url, payload, true);

            if (response && response.status) {
                const savedEventId = eventId || response.res?.data?._id;

                // Schedule notifications for the event
                if (savedEventId) {
                    try {
                        await this.service.post(
                            `${baseUrlAdmin}/event-calender/schedule-notifications`,
                            { eventId: savedEventId },
                            true
                        );
                        console.log('[saveEvent] Notifications scheduled successfully');
                    } catch (scheduleError) {
                        console.error('[saveEvent] Error scheduling notifications:', scheduleError);
                        // Don't fail the save operation if notification scheduling fails
                    }
                }

                this.showToast('success', 'Success', isEditMode ? 'Event updated successfully' : 'Event created successfully');
                this.setState({ showEventDialog: false });
                await this.fetchEvents();
            } else {
                this.showToast('error', 'Error', response?.res?.message || 'Failed to save event');
            }
        } catch (error) {
            console.error('Error saving event:', error);
            this.showToast('error', 'Error', 'An unexpected error occurred');
        } finally {
            this.setState({ loading: false });
        }
    };

    showToast = (severity, summary, detail) => {
        if (this.toast) {
            this.toast.show({
                severity,
                summary,
                detail,
                life: 3000
            });
        }
    };

    // Render event dialog footer
    renderEventDialogFooter = () => {
        const { isEditMode } = this.state;
        return (
            <div style={{
                padding: '16px 32px 24px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                backgroundColor: '#F9FAFB'
            }}>
                {isEditMode && (
                    <Button
                        style={{
                            border: 0,
                            borderRadius: '8px',
                            backgroundColor: '#DC2626',
                            padding: '12px 24px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: 'white',
                            fontWeight: '500',
                            fontSize: '14px',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                            transition: 'all 0.2s'
                        }}
                        onClick={() => {
                            this.setState({
                                showEventDialog: false,
                                showDeleteDialog: true
                            });
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#B91C1C'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#DC2626'}
                    >
                        Delete Event
                    </Button>
                )}
                <Button
                    style={{
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        padding: '12px 24px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#374151',
                        fontWeight: '500',
                        fontSize: '14px',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s'
                    }}
                    onClick={() => this.setState({ showEventDialog: false })}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                    Cancel
                </Button>
                <Button
                    style={{
                        border: 0,
                        borderRadius: '8px',
                        backgroundColor: '#2563EB',
                        padding: '12px 24px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'white',
                        fontWeight: '500',
                        fontSize: '14px',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s'
                    }}
                    onClick={this.saveEvent}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#1D4ED8'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#2563EB'}
                >
                    <AddIconInButtonFull width={16} height={16} color={'#FFFFFF'} />
                    {isEditMode ? "Update Event" : "Create Event"}
                </Button>
            </div>
        );
    };

    // Render delete dialog footer
    renderDeleteDialogFooter = () => (
        <div style={{
            padding: '16px 24px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
        }}>
            <Button
                label="Cancel"
                style={{
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    padding: '10px 20px',
                    color: '#374151',
                    fontWeight: '500',
                    fontSize: '14px'
                }}
                onClick={() => this.setState({ showDeleteDialog: false })}
            />
            <Button
                label="Delete Event"
                style={{
                    border: 0,
                    borderRadius: '8px',
                    backgroundColor: '#DC2626',
                    padding: '10px 20px',
                    color: 'white',
                    fontWeight: '500',
                    fontSize: '14px'
                }}
                onClick={this.deleteEvent}
            />
        </div>
    );

    // Render action dialog footer
    renderActionDialogFooter = () => (
        <div className="flex justify-content-center align-items-center">
            <Button
                label="Cancel"
                className='p-button-outlined'
                onClick={() => this.setState({ showActionDialog: false })}
            />
            <Button
                label="Edit"
                className='ma-m-lr10'
                onClick={this.openEditEventDialog}
            />
            <Button
                label="Delete"
                className='ma-m-lr10 p-button-danger'
                onClick={() => {
                    this.setState({
                        showActionDialog: false,
                        showDeleteDialog: true
                    });
                }}
            />
        </div>
    );

    render() {
        const {
            events, loading, showEventDialog, showActionDialog, showDeleteDialog,
            isEditMode, selectedEvent, eventForm, validationErrors, dropdownsData
        } = this.state;

        return (
            <>
                <div className='grid'>
                    {/* Left sidebar with legend */}
                    <div className='col-2 card shadow-5 my-5'>
                        <div className='flex justify-content-center'>
                            <Button
                                className='shadow-5'
                                style={{ borderRadius: 10, backgroundColor: '#076EFF' }}
                                onClick={this.openAddEventDialog}
                            >
                                <p className='addButtonClass' style={{ fontSize: '12px' }}>Add Event</p>
                            </Button>
                        </div>

                        <div className='mt-8'>
                            {/* Legend items */}
                            <div className='ml-4 mt-3'>
                                <div className='flex'>
                                    <div className="border-2" style={{ borderColor: '#076EFF', height: '20px', width: '20px' }}></div>
                                    <p className='repText font-bold ml-4' style={{ maxWidth: '80%', overflowWrap: 'break-word' }}>Today</p>
                                </div>
                            </div>
                            <div className='ml-4 mt-3'>
                                <div className='flex'>
                                    <div className="border-2" style={{ color: '#E6E8EA', height: '20px', width: '20px', backgroundColor: '#F8F9FA' }}></div>
                                    <p className='repText font-bold ml-4' style={{ maxWidth: '80%', overflowWrap: 'break-word' }}>Sunday</p>
                                </div>
                            </div>
                            <div className='ml-4 mt-3'>
                                <div className='flex'>
                                    <div className="border-2" style={{ borderColor: '#0077B6', height: '20px', width: '20px' }}></div>
                                    <p className='repText font-bold ml-4' style={{ maxWidth: '80%', overflowWrap: 'break-word' }}>Academic Event</p>
                                </div>
                            </div>
                            <div className='ml-4 mt-3'>
                                <div className='flex'>
                                    <div className="border-2" style={{ color: '#E6E8EA', height: '20px', width: '20px', backgroundColor: '#268900' }}></div>
                                    <p className='repText font-bold ml-4' style={{ maxWidth: '80%', overflowWrap: 'break-word' }}>Holiday</p>
                                </div>
                            </div>
                            <div className='ml-4 mt-3'>
                                <div className='flex'>
                                    <div className="border-2" style={{ color: '#BF0031', height: '20px', width: '20px' }}></div>
                                    <p className='repText font-bold ml-4' style={{ maxWidth: '80%', overflowWrap: 'break-word' }}>Examination</p>
                                </div>
                            </div>
                            <div className='ml-4 mt-3'>
                                <div className='flex'>
                                    <div className="border-2" style={{ color: '#FAB710', height: '20px', width: '20px' }}></div>
                                    <p className='repText font-bold ml-4' style={{ maxWidth: '80%', overflowWrap: 'break-word' }}>Sports Event</p>
                                </div>
                            </div>
                            <div className='ml-4 mt-3'>
                                <div className='flex'>
                                    <div className="border-2" style={{ color: '#A020F0', height: '20px', width: '20px' }}></div>
                                    <p className='repText font-bold ml-4' style={{ maxWidth: '80%', overflowWrap: 'break-word' }}>Cultural Event</p>
                                </div>
                            </div>
                            <div className='ml-4 mt-3'>
                                <div className='flex'>
                                    <div className="border-2" style={{ color: '#E6E8EA', height: '20px', width: '20px' }}></div>
                                    <p className='repText font-bold ml-4' style={{ maxWidth: '80%', overflowWrap: 'break-word' }}>Miscellaneous Event</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Calendar container */}
                    <div className="col-10">
                        <div className="calendar-demo">
                            <FullCalendar
                                initialDate={moment(new Date()).format('YYYY-MM-DD')}
                                initialView="dayGridMonth"
                                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin]}
                                headerToolbar={{ left: 'title', center: '', right: 'prev,next' }}
                                editable={false}
                                selectable
                                selectMirror
                                dayMaxEvents
                                events={events}
                                eventClick={this.handleEventClick}
                                dayCellClassNames={(arg) => {
                                    return arg.date.getDay() === 0 ? 'sunday-cell' : '';
                                }}
                                eventContent={(eventInfo) => (
                                    <div>
                                        <b>{eventInfo.event.title}</b>
                                        <br />
                                        <span>{eventInfo.event.extendedProps?.eventDescription}</span>
                                        {eventInfo.event.extendedProps?.venue && (
                                            <>
                                                <br />
                                                <span>📍 {eventInfo.event.extendedProps.venue}</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                </div>

                {/* Add/Edit Event Dialog */}
                <Dialog
                    visible={showEventDialog}
                    draggable={false}
                    style={{
                        width: '600px',
                        maxHeight: '90vh',
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }}
                    modal={true}
                    closable={true}
                    className="event-dialog modern-dialog"
                    header={() => (
                        <div style={{
                            padding: '24px 32px 16px',
                            borderBottom: '1px solid #E5E7EB'
                        }}>
                            <h2 style={{
                                fontSize: '28px',
                                fontWeight: '600',
                                color: '#111827',
                                margin: 0,
                                letterSpacing: '-0.5px'
                            }}>
                                {isEditMode ? "Edit Event" : "Add New Event"}
                            </h2>
                            {isEditMode && (
                                <p style={{
                                    fontSize: '14px',
                                    color: '#6B7280',
                                    margin: '8px 0 0',
                                    fontWeight: '400'
                                }}>
                                    Update event details below
                                </p>
                            )}
                        </div>
                    )}
                    onHide={() => this.setState({ showEventDialog: false })}
                    footer={this.renderEventDialogFooter()}
                >
                    <div style={{
                        padding: '24px 32px',
                        maxHeight: 'calc(90vh - 200px)',
                        overflowY: 'auto'
                    }}>
                        <div className="">
                            {/* Event Name */}
                            <div className="col-12 mb-3">
                                <label className='label mb-2' style={{ fontWeight: '500', color: '#374151' }}>
                                    Event Name<span className='ma-required'>*</span>
                                </label>
                                <div className="mt-2">
                                    <ClassetInputText
                                        id='title'
                                        // icon={<CodeLinkIcon height={22} />}
                                        className=""
                                        height="48px"
                                        width="361px"
                                        placeholder="Enter Event name"
                                        value={eventForm.title}
                                        onChange={(e) => this.handleInputChange(e.target.value, 'title')}
                                    />
                                </div>
                                {validationErrors.title && <p className="p-error">{validationErrors.title}</p>}
                            </div>

                            {/* Event Type */}
                            <div className="col-12 mb-3">
                                <label className='label mb-2'>Event Type<span className='ma-required'>*</span></label>
                                <div className="mt-2">
                                    <ClassetDropdown
                                        id='eventType'
                                        // icon={<MailIcon />}
                                        className=""
                                        height="48px"
                                        width="361px"
                                        options={EVENT_TYPES}
                                        value={eventForm.eventType}
                                        onChange={(e) => this.handleInputChange(e.target.value, 'eventType')}
                                        placeholder={<div><span className=''>Select Event Type</span></div>}
                                    />
                                </div>
                                {validationErrors.eventType && <p className="p-error">{validationErrors.eventType}</p>}
                            </div>

                            {/* Show additional fields only if event type is selected */}
                            {eventForm.eventType && (
                                <>
                                    {/* Full Day Event and Recurring toggles side by side */}
                                    <div className='col-12 mb-3'>
                                        <div className="flex align-items-center" style={{ gap: '32px' }}>
                                            {/* Left side: Full Day Event toggle */}
                                            <div className="flex align-items-center">
                                                <InputSwitch
                                                    id="fdEvent"
                                                    className=''
                                                    checked={eventForm.fdEvent}
                                                    value={eventForm.fdEvent}
                                                    onChange={(e) => this.handleInputChange(e.value, 'fdEvent')}
                                                    disabled={eventForm.eventType === 'HE' || eventForm.eventType === 'EE'}
                                                />
                                                <label className='labelToggle'>Full Day Event</label>
                                            </div>

                                            {/* Right side: Recurring toggle */}
                                            <div className="flex align-items-center">
                                                <InputSwitch
                                                    id='recurring'
                                                    className=''
                                                    checked={eventForm.recurring}
                                                    value={eventForm.recurring}
                                                    onChange={(e) => this.handleInputChange(e.value, 'recurring')}
                                                />
                                                <label className='labelToggle'>Recurring</label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Date field for non-recurring events */}
                                    {!eventForm.recurring && (
                                        <div className="col-12 mb-3">
                                            <label className='label mb-2'>Date<span className='ma-required'>*</span></label>
                                            <div className="mt-2">
                                                <ClassetCalendar
                                                    icon={<CalenderIcon height={20} />}
                                                    className=""
                                                    height="48px"
                                                    width="361px"
                                                    placeholder="Select Date"
                                                    calendarMode="single"
                                                    id="eventDate"
                                                    value={eventForm.eventDate}
                                                    onChange={(e) => this.handleInputChange(e.target.value, 'eventDate')}
                                                />
                                            </div>
                                            {validationErrors.eventDate && <p className="p-error">{validationErrors.eventDate}</p>}
                                        </div>
                                    )}

                                    {/* Start and End Time fields for non-full-day events */}
                                    {!eventForm.fdEvent && !eventForm.recurring && eventForm.eventType !== 'HE' && eventForm.eventType !== 'EE' && (
                                        <div className="col-12 flex mb-3">
                                            <div className="mr-3" style={{ width: '170px' }}>
                                                <label className='label mb-2'>Start Time<span className='ma-required'>*</span></label>
                                                <div className="mt-2">
                                                    <span className="p-input-icon-left w-full">
                                                        <ClockIcon height={20} />
                                                        <Calendar
                                                            value={eventForm.startTime}
                                                            onChange={(e) => this.handleInputChange(e.value, 'startTime')}
                                                            timeOnly
                                                            hourFormat="12"
                                                            showSeconds={false}
                                                            className="w-full"
                                                            placeholder="Select Time"
                                                            panelClassName="time-panel"
                                                            inputClassName="p-inputtext w-full"
                                                            style={{ height: '48px' }}
                                                        />
                                                    </span>
                                                </div>
                                                {validationErrors.startTime && <p className="p-error">{validationErrors.startTime}</p>}
                                            </div>
                                            <div style={{ width: '170px' }}>
                                                <label className='label mb-2'>End Time<span className='ma-required'>*</span></label>
                                                <div className="mt-2">
                                                    <span className="p-input-icon-left w-full">
                                                        <ClockIcon height={20} />
                                                        <Calendar
                                                            value={eventForm.endTime}
                                                            onChange={(e) => this.handleInputChange(e.value, 'endTime')}
                                                            timeOnly
                                                            hourFormat="12"
                                                            showSeconds={false}
                                                            className="w-full"
                                                            placeholder="Select Time"
                                                            panelClassName="time-panel"
                                                            inputClassName="p-inputtext w-full"
                                                            style={{ height: '48px' }}
                                                        />
                                                    </span>
                                                </div>
                                                {validationErrors.endTime && <p className="p-error">{validationErrors.endTime}</p>}
                                            </div>
                                        </div>
                                    )}

                                    {/* Recurring event fields */}
                                    {eventForm.recurring && (
                                        <>
                                            <div className="col-12 mb-3">
                                                <label className='label mb-2'>Start On<span className='ma-required'>*</span></label>
                                                <div className="mt-2">
                                                    <ClassetCalendar
                                                        icon={<CalenderIcon height={20} />}
                                                        className=""
                                                        height="48px"
                                                        width="361px"
                                                        placeholder="Select Date"
                                                        calendarMode="single"
                                                        id="recStartDate"
                                                        value={eventForm.recStartDate}
                                                        onChange={(e) => this.handleInputChange(e.target.value, 'recStartDate')}
                                                    />
                                                </div>
                                                {validationErrors.recStartDate && <p className="p-error">{validationErrors.recStartDate}</p>}
                                            </div>

                                            {/* Start and End Time fields for recurring events that are not full day */}
                                            {!eventForm.fdEvent && eventForm.eventType !== 'HE' && eventForm.eventType !== 'EE' && (
                                                <div className="col-12 flex mb-3">
                                                    <div className="mr-3" style={{ width: '170px' }}>
                                                        <label className='label mb-2'>Start Time<span className='ma-required'>*</span></label>
                                                        <div className="mt-2">
                                                            <span className="p-input-icon-left w-full">
                                                                <ClockIcon height={20} />
                                                                <Calendar
                                                                    value={eventForm.startTime}
                                                                    onChange={(e) => this.handleInputChange(e.value, 'startTime')}
                                                                    timeOnly
                                                                    hourFormat="12"
                                                                    showSeconds={false}
                                                                    className="w-full"
                                                                    placeholder="Select Time"
                                                                    panelClassName="time-panel"
                                                                    inputClassName="p-inputtext w-full"
                                                                    style={{ height: '48px' }}
                                                                />
                                                            </span>
                                                        </div>
                                                        {validationErrors.startTime && <p className="p-error">{validationErrors.startTime}</p>}
                                                    </div>
                                                    <div style={{ width: '170px' }}>
                                                        <label className='label mb-2'>End Time<span className='ma-required'>*</span></label>
                                                        <div className="mt-2">
                                                            <span className="p-input-icon-left w-full">
                                                                <ClockIcon height={20} />
                                                                <Calendar
                                                                    value={eventForm.endTime}
                                                                    onChange={(e) => this.handleInputChange(e.value, 'endTime')}
                                                                    timeOnly
                                                                    hourFormat="12"
                                                                    showSeconds={false}
                                                                    className="w-full"
                                                                    placeholder="Select Time"
                                                                    panelClassName="time-panel"
                                                                    inputClassName="p-inputtext w-full"
                                                                    style={{ height: '48px' }}
                                                                />
                                                            </span>
                                                        </div>
                                                        {validationErrors.endTime && <p className="p-error">{validationErrors.endTime}</p>}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="col-12 mb-3">
                                                <label className='label mb-2'>Ends On<span className='ma-required'>*</span></label>
                                                <div className="mt-2">
                                                    <ClassetCalendar
                                                        icon={<CalenderIcon height={20} />}
                                                        className=""
                                                        height="48px"
                                                        width="361px"
                                                        placeholder="Select Date"
                                                        calendarMode="single"
                                                        id="recEndDate"
                                                        value={eventForm.recEndDate}
                                                        onChange={(e) => this.handleInputChange(e.target.value, 'recEndDate')}
                                                    />
                                                </div>
                                                {validationErrors.recEndDate && <p className="p-error">{validationErrors.recEndDate}</p>}
                                            </div>

                                            <div className="col-12 mb-3">
                                                <label className='label mb-2'>Repeat<span className='ma-required'>*</span></label>
                                                <div className="mt-2">
                                                    <ClassetDropdown
                                                        id='repeat'
                                                        icon={<MailIcon />}
                                                        className=""
                                                        height="48px"
                                                        width="361px"
                                                        options={REPEAT_OPTIONS}
                                                        value={eventForm.repeat}
                                                        onChange={(e) => this.handleInputChange(e.target.value, 'repeat')}
                                                        placeholder={<div><span className=''>Select Repeat</span></div>}
                                                    />
                                                </div>
                                                {validationErrors.repeat && <p className="p-error">{validationErrors.repeat}</p>}
                                            </div>
                                        </>
                                    )}

                                    {/* Event Description */}
                                    <div className="col-12 mb-3">
                                        <label className='label'>Event Description<span className='ma-required'>*</span></label>
                                        <div className='mt-2'>
                                            <ClassetTextArea
                                                id="eventDescription"
                                                className='border-round-md border-none'
                                                height={50}
                                                value={eventForm.eventDescription}
                                                onChange={(e) => this.handleInputChange(e.target.value, 'eventDescription')}
                                                placeholder="Description"
                                            />
                                        </div>
                                        {validationErrors.eventDescription && <p className="p-error">{validationErrors.eventDescription}</p>}
                                    </div>

                                    {/* Venue */}
                                    <div className="col-12 mb-3">
                                        <label className='label'>Venue<span className='ma-required'>*</span></label>
                                        <div className='mt-2'>
                                            <ClassetInputText
                                                id="venue"
                                                className=""
                                                height="48px"
                                                width="361px"
                                                value={eventForm.venue}
                                                onChange={(e) => this.handleInputChange(e.target.value, 'venue')}
                                                placeholder="Enter event venue"
                                            />
                                        </div>
                                        {validationErrors.venue && <p className="p-error">{validationErrors.venue}</p>}
                                    </div>

                                    {/* Branch to Section Dropdowns */}
                                    <div className="col-12 mb-3">
                                        <BranchToSectionMultiDDTillSec
                                            setValues={this.setDropdownValues}
                                            dropdownsData={dropdownsData}
                                        />
                                        {validationErrors.boardIds && <p className="p-error">{validationErrors.boardIds}</p>}
                                        {validationErrors.branchIds && <p className="p-error">{validationErrors.branchIds}</p>}
                                        {validationErrors.classIds && <p className="p-error">{validationErrors.classIds}</p>}
                                        {validationErrors.sectionIds && <p className="p-error">{validationErrors.sectionIds}</p>}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Dialog>

                {/* Action Dialog */}
                <Dialog
                    header="Event Actions"
                    visible={showActionDialog}
                    style={{ width: '400px' }}
                    modal
                    footer={this.renderActionDialogFooter()}
                    onHide={() => this.setState({ showActionDialog: false })}
                >
                </Dialog>

                {/* Delete Dialog */}
                <Dialog
                    visible={showDeleteDialog}
                    style={{
                        width: '450px',
                        borderRadius: '16px'
                    }}
                    modal
                    className="modern-dialog"
                    header={() => (
                        <div style={{
                            padding: '24px 24px 16px',
                            borderBottom: '1px solid #E5E7EB'
                        }}>
                            <h3 style={{
                                fontSize: '20px',
                                fontWeight: '600',
                                color: '#111827',
                                margin: 0
                            }}>
                                Delete Event
                            </h3>
                        </div>
                    )}
                    footer={this.renderDeleteDialogFooter()}
                    onHide={() => this.setState({ showDeleteDialog: false })}
                >
                    <div style={{ padding: '24px' }}>
                        <p style={{
                            fontSize: '15px',
                            color: '#6B7280',
                            lineHeight: '1.6',
                            margin: 0
                        }}>
                            Are you sure you want to delete this event? This action cannot be undone.
                        </p>
                    </div>
                </Dialog>

                {loading && <LoadingComponent />}
                <Toast ref={(el) => this.toast = el} position="bottom-right" />
            </>
        );
    }
}


export default AcademicCalendar;